import { useMemo, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useProjectTasks } from '../../../api/hooks/useProjects';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import {
  useProjectKanbanBoards, unwrapProjectBoards, normalizeKanbanState, KANBAN_STATES,
} from '../../../api/hooks/useKanban';
import { PmCard, PmInfoBox, PmViewToggle } from '../../../components/pm';
import { ClosedBoardsSection } from '../components/fortschritt/ClosedBoardsSection';

dayjs.extend(isoWeek);

interface Task {
  id: string;
  state?: string;
  status?: string;
  done_at?: string | null;
  closed_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface BurnPoint {
  date: string;
  done: number;
  scope: number;
}

/** Aggregiertes Wochen-Datenpunkt für die KW-Achse — kumulativ, daher zählt pro Woche der letzte Tageswert. */
interface WeekPoint {
  label: string;
  sortKey: string;
  done: number;
  scope: number;
}

type ZoomMode = 'day' | 'week';

const DONE_STATES = new Set(['DONE', 'CLOSED', 'COMPLETED', 'done', 'closed', 'completed']);

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function buildBurnUp(tasks: Task[]): BurnPoint[] {
  if (tasks.length === 0) return [];

  const created = tasks
    .map((t) => t.created_at)
    .filter((s): s is string => !!s)
    .sort();
  // pm-api speichert aktuell keinen dedizierten done_at/closed_at/completed_at-Timestamp —
  // fallback auf updated_at wenn state DONE-ähnlich ist (das ist der Zeitpunkt der letzten
  // Status-Änderung, was für die Burn-Up-Kurve ausreicht).
  const completed = tasks
    .filter((t) => DONE_STATES.has(t.state ?? '') || DONE_STATES.has(t.status ?? ''))
    .map((t) => t.done_at ?? t.closed_at ?? t.completed_at ?? t.updated_at ?? t.created_at ?? '')
    .filter(Boolean);

  // Auf Tagesgrenze normalisieren: Start = Anfang vom ersten created-Tag, End = Ende von heute.
  // Sonst läuft die Schleife bei kurzen Zeiträumen nur 1× durch und das Chart zeigt einen
  // unsichtbaren Einzelpunkt.
  const startDay = new Date(created[0] ?? new Date().toISOString());
  startDay.setUTCHours(0, 0, 0, 0);
  const endDay = new Date();
  endDay.setUTCHours(23, 59, 59, 999);
  const days: BurnPoint[] = [];
  for (let d = new Date(startDay); d <= endDay; d.setUTCDate(d.getUTCDate() + 1)) {
    const stamp = isoDay(d);
    const scope = tasks.filter((t) => (t.created_at ?? '') <= stamp + 'T23:59:59Z').length;
    const done = completed.filter((c) => c.slice(0, 10) <= stamp).length;
    days.push({ date: stamp, scope, done });
  }
  return days;
}

/**
 * Verdichtet die Tages-Serie auf Kalenderwochen (ISO, Muster aus PmProjectGantt).
 * Da die Kurve kumulativ ist, entspricht der letzte Tageswert einer Woche zugleich
 * ihrem Maximum — es muss nicht separat aggregiert werden.
 */
function buildWeeklySeries(series: BurnPoint[]): WeekPoint[] {
  const byWeek = new Map<string, WeekPoint>();
  for (const p of series) {
    const d = dayjs(p.date);
    const sortKey = `${d.isoWeekYear()}-${String(d.isoWeek()).padStart(2, '0')}`;
    byWeek.set(sortKey, { label: `KW ${d.isoWeek()}`, sortKey, done: p.done, scope: p.scope });
  }
  return Array.from(byWeek.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

/** Wählt aus einer Label-Liste eine überlappungsfreie Teilmenge (immer inkl. erstem/letztem Punkt). */
function thinLabels<T>(items: T[], maxLabels: number): number[] {
  if (items.length <= maxLabels) return items.map((_, i) => i);
  const step = Math.ceil(items.length / maxLabels);
  const indices = new Set<number>();
  for (let i = 0; i < items.length; i += step) indices.add(i);
  indices.add(items.length - 1);
  return Array.from(indices).sort((a, b) => a - b);
}

/** Anzahl Aufgaben je Kanban-Spalte, summiert über alle Arbeitspakete (PHQ-90). */
export type KanbanColumnSums = Record<'PLANNED' | 'IN_PROGRESS' | 'COMPLETED', number>;

/**
 * Summiert die Karten der übergebenen (aktiven) Boards je Spalte. Ausgeblendete und
 * abgebrochene Karten zählen nicht mit — wie bei der AP-Status-Ableitung im Backend.
 * Alte Zustände werden auf die verbleibenden drei Spalten abgebildet (normalizeKanbanState).
 */
export function sumKanbanColumns(
  boards: ReadonlyArray<{ cards: ReadonlyArray<{ state: Parameters<typeof normalizeKanbanState>[0]; hidden: boolean }> }>,
): KanbanColumnSums {
  const sums: KanbanColumnSums = { PLANNED: 0, IN_PROGRESS: 0, COMPLETED: 0 };
  for (const board of boards) {
    for (const card of board.cards) {
      if (card.hidden || card.state === 'CANCELED') continue;
      const col = normalizeKanbanState(card.state);
      if (col in sums) sums[col as keyof KanbanColumnSums] += 1;
    }
  }
  return sums;
}

const KANBAN_COLUMN_STYLE: Record<keyof KanbanColumnSums, { label: string; color: string }> = {
  PLANNED: { label: 'Geplant', color: 'var(--pm-gray-400)' },
  IN_PROGRESS: { label: 'In Arbeit', color: 'var(--pm-info)' },
  COMPLETED: { label: 'Erledigt', color: 'var(--pm-success)' },
};

export function BurnUpTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { data, isLoading } = useProjectTasks(projectId);
  const tasks = ((data as unknown as { data?: Task[] } | undefined)?.data ?? []);
  const [zoom, setZoom] = useState<ZoomMode>('day');

  // PHQ-90: Aufgaben liegen als Karten auf den Kanban-Boards der APs — die drei Spalten
  // werden über alle APs summiert (nur aktive Boards, eingefrorene sind Kopien).
  const { data: boardsData, isLoading: boardsLoading } = useProjectKanbanBoards(projectId);
  const columnSums = useMemo(() => sumKanbanColumns(unwrapProjectBoards(boardsData)), [boardsData]);
  const kanbanTotal = columnSums.PLANNED + columnSums.IN_PROGRESS + columnSums.COMPLETED;

  // Reifegrad Schritt 15 (BurnUp) anhand Anzahl Tasks
  useSyncMaturityFromCount(projectId, 15, isLoading ? -1 : tasks.length);

  const series = useMemo(() => buildBurnUp(tasks), [tasks]);
  const weekSeries = useMemo(() => buildWeeklySeries(series), [series]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }
  if (series.length === 0 && kanbanTotal === 0 && !boardsLoading) {
    return (
      <Stack spacing={2.5}>
        <ClosedBoardsSection projectId={projectId} />
        <PmInfoBox variant="info" title={t('burnup.noData', 'Keine Daten verfügbar')}>
          {t('burnup.noDataHint', 'Es sind noch keine Aufgaben angelegt — sobald Aufgaben auf den Kanban-Boards der Arbeitspakete erstellt und erledigt werden, erscheinen sie hier.')}
        </PmInfoBox>
      </Stack>
    );
  }

  // Kanban-Summen als Balken (Geplant / In Arbeit / Erledigt über alle Arbeitspakete).
  const kanbanCard = (
    <PmCard>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('burnup.kanbanTitle', 'Aufgaben nach Kanban-Spalte')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
            {kanbanTotal} {t('burnup.tasks', 'Aufgaben')} · {t('burnup.kanbanScope', 'Summe über alle Arbeitspakete')}
          </Typography>
        </Box>
        {KANBAN_STATES.map((state) => {
          const col = state as keyof KanbanColumnSums;
          const count = columnSums[col];
          const style = KANBAN_COLUMN_STYLE[col];
          return (
            <Box key={state} data-testid={`burnup-bar-${state}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ width: 90, flexShrink: 0 }}>
                {t(`burnup.column_${state}`, style.label)}
              </Typography>
              <Box sx={{ flex: 1, height: 22, background: 'var(--pm-bg-deep)', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  data-testid={`burnup-fill-${state}`}
                  sx={{ height: '100%', width: `${kanbanTotal > 0 ? (count / kanbanTotal) * 100 : 0}%`, background: style.color, minWidth: count > 0 ? 4 : 0 }}
                />
              </Box>
              <Typography variant="body2" sx={{ width: 36, textAlign: 'right', fontFamily: 'monospace' }}>{count}</Typography>
            </Box>
          );
        })}
      </Stack>
    </PmCard>
  );

  if (series.length === 0) {
    return <Stack spacing={2.5}><ClosedBoardsSection projectId={projectId} />{kanbanCard}</Stack>;
  }

  const W = 800;
  const H = 320;
  const padL = 48;
  const padR = 16;
  const padT = 16;
  const padB = 32;

  // Aktive Punktreihe je nach Zoom-Modus: Tag = jeder Tag ein Punkt (Detailansicht,
  // unverändert gegenüber der ursprünglichen Tages-Ansicht); Woche = KW-verdichtet.
  const activePoints: Array<{ label: string; done: number; scope: number }> =
    zoom === 'week' ? weekSeries : series.map((p) => ({ label: p.date, done: p.done, scope: p.scope }));

  const maxScope = Math.max(...activePoints.map((p) => p.scope), 1);
  const xs = activePoints.map((_, i) => padL + (i / Math.max(activePoints.length - 1, 1)) * (W - padL - padR));
  const ysDone = activePoints.map((p) => H - padB - (p.done / maxScope) * (H - padT - padB));
  const ysScope = activePoints.map((p) => H - padB - (p.scope / maxScope) * (H - padT - padB));

  const path = (ys: number[]) =>
    ys.map((y, i) => `${i === 0 ? 'M' : 'L'} ${xs[i]} ${y}`).join(' ');

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(maxScope * p));

  // X-Achsen-Beschriftung: Tag-Ansicht zeigt wie bisher nur Start-/End-Datum;
  // Woche-Ansicht zeigt „KW n" an einer überlappungsfreien Auswahl von Punkten.
  const xLabelIndices = zoom === 'week' ? thinLabels(activePoints, 14) : [0, activePoints.length - 1];

  return (
    <Stack spacing={2.5}>
      <ClosedBoardsSection projectId={projectId} />
      {kanbanCard}
      <PmCard>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('burnup.title', 'Fortschritt')}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                {series.length} {t('burnup.days', 'Tage')} · {tasks.length} {t('burnup.tasks', 'Aufgaben')}
              </Typography>
              <PmViewToggle<ZoomMode>
                value={zoom}
                onChange={setZoom}
                options={[
                  { value: 'day', label: t('burnup.zoomDay', 'Zoom: Tage'), icon: <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{t('burnup.zoomDayShort', 'Tag')}</Typography> },
                  { value: 'week', label: t('burnup.zoomWeek', 'Zoom: Kalenderwochen'), icon: <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{t('burnup.zoomWeekShort', 'KW')}</Typography> },
                ]}
              />
            </Stack>
          </Box>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 600 }}>
              {/* Y-Achsen-Gridlines + Labels */}
              {ticks.map((tick, i) => {
                const y = H - padB - ((tick / maxScope) * (H - padT - padB));
                return (
                  <g key={i}>
                    <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--pm-bg-deep)" strokeDasharray="2,4" />
                    <text x={padL - 8} y={y + 4} fontSize="11" textAnchor="end" fill="var(--pm-fg-subtle)">{tick}</text>
                  </g>
                );
              })}
              {/* X-Achse: Tag-Ansicht = Datum-Endpunkte, KW-Ansicht = „KW n"-Ticks */}
              {zoom === 'day'
                ? xLabelIndices.map((i) => (
                    <text
                      key={i}
                      x={xs[i]}
                      y={H - 8}
                      fontSize="11"
                      textAnchor={i === 0 ? 'start' : 'end'}
                      fill="var(--pm-fg-subtle)"
                    >
                      {activePoints[i]!.label}
                    </text>
                  ))
                : xLabelIndices.map((i) => (
                    <g key={i}>
                      <line x1={xs[i]} y1={padT} x2={xs[i]} y2={H - padB} stroke="var(--pm-bg-deep)" strokeDasharray="1,4" opacity={0.5} />
                      <text x={xs[i]} y={H - 8} fontSize="10" textAnchor="middle" fill="var(--pm-fg-subtle)">
                        {activePoints[i]!.label}
                      </text>
                    </g>
                  ))}
              {/* Scope-Linie (oben) */}
              <path d={path(ysScope)} fill="none" stroke="var(--pm-fg-subtle)" strokeWidth="1.5" strokeDasharray="4,3" />
              {/* Done-Fläche */}
              <path
                d={`${path(ysDone)} L ${xs[xs.length - 1]} ${H - padB} L ${xs[0]} ${H - padB} Z`}
                fill="rgba(95,196,216,0.18)"
              />
              <path d={path(ysDone)} fill="none" stroke="var(--rittec-petrol)" strokeWidth="2" />
            </svg>
          </Box>
          <Stack direction="row" spacing={2.5} sx={{ pl: 6, fontSize: '0.78rem', color: 'var(--pm-fg-muted)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 3, background: 'var(--rittec-petrol)' }} />
              {t('burnup.done', 'Erledigte Aufgaben (kumuliert)')}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 2, background: 'var(--pm-fg-subtle)', backgroundImage: 'repeating-linear-gradient(90deg, var(--pm-fg-subtle) 0 4px, transparent 4px 7px)' }} />
              {t('burnup.scope', 'Scope (Gesamtaufgaben)')}
            </Box>
          </Stack>
        </Stack>
      </PmCard>
    </Stack>
  );
}
